import { getState } from '../store';
import { leaveGame, sendChatMessage } from '../api/game-api';
import { navigate } from '../router';
import { getTheme, applyTheme } from './theme-toggle';
import { disconnectTeamWs } from '../api/team-sync';
import { triggerAdvance } from '../utils/auto-advance';
import type { ViewLifecycle } from '../utils/view-lifecycle';

/** Minimal HTML escape — the rules dialog renders state.gameRules inside a
 *  `<pre>` with white-space:pre-wrap. Same policy as lobby-view.ts which
 *  already escapes the same field. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!
  ));
}

/**
 * Game menu component — kebab (⋮) dropdown shown on all in-game screens
 * (Location phases, Chat). Hosts non-primary actions:
 *   • SOS (emergency) — only when `state.organizerPhone` is set
 *   • Theme toggle (dark ⇄ light)
 *   • Leave game (destructive)
 *
 * Creates its own elements under `document.body` so the menu + SOS dialog
 * escape view-level clipping / z-index stacking. `destroy()` removes them.
 *
 * SOS moved from standalone button → menu item in session 47e: reduces
 * header clutter and unifies all game-level actions behind one entry point.
 */
export interface GameMenuOptions {
  /** When true, the kebab renders the captain-only "Пропустить этап" item
   *  (gated additionally on `state.allowManualSkip && state.isCaptain &&
   *  state.status === 'ACTIVE'`). Only the location-view should pass true;
   *  chat-view explicitly disables it so a captain mid-conversation doesn't
   *  get yanked out of chat by a misclick. Default false. */
  allowSkip?: boolean;
}

export function createGameMenu(container: HTMLElement, lc: ViewLifecycle, options: GameMenuOptions = {}): { destroy: () => void } {
  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position: relative; display: inline-block;';

  const btn = document.createElement('button');
  btn.className = 'btn btn-outline';
  btn.style.cssText = 'min-height: 40px; width: 40px; padding: 0; font-size: 20px; display: flex; align-items: center; justify-content: center;';
  btn.textContent = '\u22EE';
  btn.setAttribute('aria-label', 'Меню');
  wrapper.appendChild(btn);

  const menu = document.createElement('div');
  menu.setAttribute('role', 'menu');
  // Use --bg-card and --text-primary (both defined in dark AND light themes
  // in theme/styles.css). The previous --bg-elevated was never defined, so
  // the menu stayed dark in light mode and the "Тёмная тема" label became
  // near-invisible on dark fallback bg.
  menu.style.cssText = 'display: none; position: fixed; background: var(--bg-card); border: 1px solid var(--amber); border-radius: 8px; padding: 4px 0; min-width: 220px; z-index: 100; box-shadow: 0 8px 24px rgba(0,0,0,0.5);';
  document.body.appendChild(menu);

  // SOS dialog — created once, reused by every SOS click. Only attached
  // when organizerPhone is present (matches the old sos-button gate).
  const state = getState();
  const hasSos = !!state.organizerPhone;
  const hasRules = !!(state.gameRules && state.gameRules.trim());
  // Skip is captain-only (backend enforces — session 55) and requires the
  // organizer to have enabled it. Only meaningful while the game is ACTIVE
  // and the player is on a real location. Moved here from the inline
  // location-view button so the primary action area stays clean.
  const currentLoc = state.locations[state.currentLocationIndex];
  const hasSkip = !!options.allowSkip
    && state.allowManualSkip && state.isCaptain
    && state.status === 'ACTIVE' && !!currentLoc;

  // Rules dialog — created once if the game has rules, reused by every
  // "Правила игры" click. Mirrors the sosDialog pattern (fixed modal
  // attached to body so it escapes view-level clipping).
  let rulesDialog: HTMLElement | null = null;
  let onRulesBackdrop: ((e: Event) => void) | null = null;
  if (hasRules) {
    rulesDialog = document.createElement('div');
    rulesDialog.className = 'rules-dialog modal-overlay';
    rulesDialog.style.display = 'none';
    // Long rulesets can easily exceed a screen — inner scroll with max-height
    // so mobile users can actually read them without the dialog blowing past
    // the viewport bottom.
    rulesDialog.innerHTML = `
      <div class="modal-dialog" style="max-width: 560px; max-height: 80vh; display: flex; flex-direction: column;">
        <h3 style="margin-bottom: var(--sp-md);">Правила игры</h3>
        <div class="rules-body text-body" style="white-space: pre-wrap; overflow-y: auto; flex: 1; margin-bottom: var(--sp-lg);"></div>
        <button class="rules-close-btn btn btn-secondary">Закрыть</button>
      </div>
    `;
    document.body.appendChild(rulesDialog);
    rulesDialog.querySelector<HTMLElement>('.rules-body')!.innerHTML = escapeHtml(state.gameRules!);
    const closeBtn = rulesDialog.querySelector<HTMLButtonElement>('.rules-close-btn')!;
    closeBtn.addEventListener('click', () => { if (rulesDialog) rulesDialog.style.display = 'none'; });
    onRulesBackdrop = (e: Event) => { if (e.target === rulesDialog && rulesDialog) rulesDialog.style.display = 'none'; };
    rulesDialog.addEventListener('click', onRulesBackdrop);
  }

  let sosDialog: HTMLElement | null = null;
  let onSosBackdrop: ((e: Event) => void) | null = null;
  if (hasSos) {
    sosDialog = document.createElement('div');
    sosDialog.className = 'sos-dialog modal-overlay';
    sosDialog.style.display = 'none';
    sosDialog.innerHTML = `
      <div class="modal-dialog">
        <h3 style="margin-bottom: var(--sp-md);">Экстренный вызов</h3>
        <p class="text-body" style="margin-bottom: var(--sp-lg);">Позвонить организатору по экстренному поводу?</p>
        <div style="display: flex; gap: var(--sp-md);">
          <a class="sos-call-link btn btn-primary" href="tel:" style="flex: 1; text-align: center; text-decoration: none;">ПОЗВОНИТЬ</a>
          <button class="sos-cancel-btn btn btn-secondary" style="flex: 1;">Отмена</button>
        </div>
      </div>
    `;
    document.body.appendChild(sosDialog);
    const sanitizedPhone = state.organizerPhone.replace(/[^+\d]/g, '');
    const callLink = sosDialog.querySelector<HTMLAnchorElement>('.sos-call-link')!;
    callLink.href = `tel:${sanitizedPhone}`;
    const cancelBtn = sosDialog.querySelector<HTMLButtonElement>('.sos-cancel-btn')!;
    cancelBtn.addEventListener('click', () => { if (sosDialog) sosDialog.style.display = 'none'; });
    onSosBackdrop = (e: Event) => { if (e.target === sosDialog && sosDialog) sosDialog.style.display = 'none'; };
    sosDialog.addEventListener('click', onSosBackdrop);
  }

  // --- Menu items ----------------------------------------------------
  // "Same style as Выйти из игры" — shared padding/font/size. Variants:
  //   • danger (red): destructive / emergency actions
  //   • default: neutral actions (theme toggle)
  function makeItem(opts: { label: string; danger?: boolean; onClick: () => void | Promise<void>; }): HTMLButtonElement {
    const item = document.createElement('button');
    item.setAttribute('role', 'menuitem');
    // Both tokens are defined in dark AND light themes (theme/styles.css),
    // so no hardcoded fallback — the fallback was bright white, which looked
    // wrong on the light-theme light card background.
    const color = opts.danger ? 'var(--danger)' : 'var(--text-primary)';
    item.style.cssText = `display: block; width: 100%; padding: 12px 16px; background: none; border: none; color: ${color}; font-size: 14px; text-align: left; cursor: pointer; white-space: nowrap;`;
    item.textContent = opts.label;
    item.addEventListener('click', () => {
      // v0.79.40 (2026-05-03) — invoke the action FIRST, hide the menu
      // AFTER. iOS Safari is strict about the user-gesture activation
      // chain: hiding the trigger element BEFORE a synchronous `confirm()`
      // dialog inside the action handler can suppress the dialog (the
      // gesture token is considered "consumed" by the DOM mutation).
      // Live game 983541 «Апр»: pre-fix the «Пропустить этап (со штрафом)»
      // item showed no dialog and fired no /advance request — pure silent
      // failure. TIMEOUT path (no confirm, programmatic) worked fine.
      //
      // The async parts of opts.onClick (await fetch) yield AFTER the
      // synchronous prefix (confirm + triggerAdvance up to first await),
      // so menu-hide runs after that prefix completes — UI still feels
      // responsive: confirm dialog shows over the still-visible menu,
      // user clicks OK, fetch fires, then menu closes once control
      // returns to this handler.
      void opts.onClick();
      open = false;
      menu.style.display = 'none';
    });
    return item;
  }

  // Item order (top → bottom): rules (if present) → theme toggle → leave → SOS.
  // Rules first — informational and the most common mid-game question
  // ("что я должен делать?"). Theme toggle next. SOS sits at the very bottom
  // per user request — puts the emergency action at the deepest thumb-reach
  // position on mobile and keeps destructive-but-recoverable "Выйти" above
  // it as a visual separator.

  // Rules item — only rendered if the game has non-empty rules. Opens a
  // scrollable modal; players can dismiss with the button or by tapping
  // the backdrop.
  if (hasRules && rulesDialog) {
    const rulesItem = makeItem({
      label: '\uD83D\uDCD6 Правила игры',
      onClick: () => { if (rulesDialog) rulesDialog.style.display = 'flex'; },
    });
    menu.appendChild(rulesItem);
  }

  // Theme toggle item — label reflects the target state, not current,
  // so users click "switch to light" rather than tapping the current one.
  const themeItem = makeItem({
    label: themeItemLabel(),
    onClick: () => {
      applyTheme(getTheme() === 'dark' ? 'light' : 'dark');
      themeItem.textContent = themeItemLabel();
    },
  });
  menu.appendChild(themeItem);

  // Skip stage — captain-only; only appears when the organizer enabled
  // manual skip and the player is on an active location. Confirm with the
  // penalty formatted as mm:ss so the captain knows the cost. Renders above
  // "Выйти из игры" so destructive actions group at the bottom.
  if (hasSkip && currentLoc) {
    const timeLimitSec = currentLoc.timeLimitSec;
    const m = Math.floor(timeLimitSec / 60);
    const s = timeLimitSec % 60;
    const penaltyStr = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    const skipItem = makeItem({
      label: '\u23ED\uFE0F Пропустить этап (со штрафом)',
      danger: true,
      onClick: () => {
        if (!confirm(`Пропустить этап? К итоговому времени добавится штраф ${penaltyStr}.`)) return;
        triggerAdvance('SKIP', lc);
      },
    });
    menu.appendChild(skipItem);
  }

  // Leave game — destructive but recoverable (confirm dialog).
  const leaveItem = makeItem({
    // Door emoji matches the icon-prefixed style of SOS (📞) and the theme
    // toggle (☀️/🌙) — keeps the menu visually consistent.
    label: '\uD83D\uDEAA Выйти из игры',
    danger: true,
    onClick: async () => {
      if (!confirm('Вы уверены? Прогресс будет потерян.')) return;
      const s = getState();
      try { await leaveGame(s.gameCode, s.playerId); } catch { /* best-effort */ }
      // Session 50: close team WS so subsequent logins don't see stale events.
      disconnectTeamWs();
      navigate('join');
    },
  });
  menu.appendChild(leaveItem);

  // SOS item — last; fires the same logic the old standalone button did.
  let sosItem: HTMLButtonElement | null = null;
  if (hasSos) {
    sosItem = makeItem({
      label: '\uD83D\uDCDE SOS — экстренный вызов',
      danger: true,
      onClick: () => {
        const s = getState();
        const sendSos = (msg: string) => {
          sendChatMessage(s.gameCode, s.playerId, msg, true).catch(() => { /* best-effort */ });
        };
        // Check the method, not `'geolocation' in navigator`: jsdom + some
        // restricted embed contexts (permissions-policy, cross-origin iframes)
        // expose the property with an undefined value, so the `in` check
        // lied and crashed at .getCurrentPosition.
        const geo = navigator.geolocation;
        if (geo && typeof geo.getCurrentPosition === 'function') {
          geo.getCurrentPosition(
            (pos) => {
              const lat = pos.coords.latitude.toFixed(6);
              const lon = pos.coords.longitude.toFixed(6);
              sendSos(`\uD83C\uDD98 ЭКСТРЕННЫЙ ВЫЗОВ\nКоординаты: ${lat}, ${lon}\nhttps://yandex.ru/maps/?pt=${lon},${lat}&z=16`);
            },
            () => sendSos('\uD83C\uDD98 ЭКСТРЕННЫЙ ВЫЗОВ (координаты недоступны)'),
            { timeout: 5000, enableHighAccuracy: true }
          );
        } else {
          sendSos('\uD83C\uDD98 ЭКСТРЕННЫЙ ВЫЗОВ (координаты недоступны)');
        }
        if (sosDialog) sosDialog.style.display = 'flex';
      },
    });
    menu.appendChild(sosItem);
  }

  // --- Open/close plumbing -------------------------------------------
  let open = false;
  const positionMenu = () => {
    const rect = btn.getBoundingClientRect();
    menu.style.top = (rect.bottom + 4) + 'px';
    menu.style.right = (window.innerWidth - rect.right) + 'px';
    menu.style.left = 'auto';
  };
  const toggle = (e?: Event) => {
    e?.stopPropagation();
    open = !open;
    if (open) positionMenu();
    menu.style.display = open ? 'block' : 'none';
  };
  const closeMenu = (e: Event) => {
    if (!open) return;
    const target = e.target as Node;
    // Don't close on clicks inside the menu itself — item click handlers
    // close explicitly. Needed because the menu is attached to document.body,
    // not inside wrapper.
    if (wrapper.contains(target) || menu.contains(target)) return;
    open = false;
    menu.style.display = 'none';
  };

  btn.addEventListener('click', toggle);
  document.addEventListener('click', closeMenu);

  container.appendChild(wrapper);

  return {
    destroy() {
      btn.removeEventListener('click', toggle);
      document.removeEventListener('click', closeMenu);
      if (sosDialog && onSosBackdrop) sosDialog.removeEventListener('click', onSosBackdrop);
      sosDialog?.remove();
      if (rulesDialog && onRulesBackdrop) rulesDialog.removeEventListener('click', onRulesBackdrop);
      rulesDialog?.remove();
      menu.remove();
      wrapper.remove();
    }
  };
}

/** Label reflects the action (what clicking will do), not current state. */
function themeItemLabel(): string {
  return getTheme() === 'dark'
    ? '\u2600\uFE0F Светлая тема'
    : '\uD83C\uDF19 Тёмная тема';
}
